import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Eye } from "lucide-react";
import { cn } from "../../lib/utils";
import type { GraphRunEvent } from "../../hooks/useGraphRun";

interface StateInspectorProps {
  events: GraphRunEvent[];
  className?: string;
}

function JsonNode({
  keyName,
  value,
  depth = 0,
  changed = false,
}: {
  keyName: string | null;
  value: unknown;
  depth?: number;
  changed?: boolean;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const isObject = value !== null && typeof value === "object";
  const isArray = Array.isArray(value);
  const entries = isObject ? Object.entries(value as Record<string, unknown>) : [];

  if (!isObject) {
    const colorClass =
      typeof value === "string"
        ? "text-emerald-500"
        : typeof value === "number"
          ? "text-blue-400"
          : typeof value === "boolean"
            ? "text-amber-400"
            : "text-muted-foreground";
    return (
      <div className={cn("flex items-baseline gap-1 py-0.5", changed && "bg-yellow-500/10 rounded px-1")} style={{ paddingLeft: depth * 16 }}>
        {keyName !== null && <span className="text-foreground/60">{keyName}:</span>}
        <span className={cn("font-mono", colorClass)}>
          {typeof value === "string" ? `"${value}"` : String(value ?? "null")}
        </span>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={cn("flex items-center gap-1 py-0.5 hover:bg-accent/30 w-full text-left rounded", changed && "bg-yellow-500/10")}
        style={{ paddingLeft: depth * 16 }}
      >
        {expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
        {keyName !== null && <span className="text-foreground/60">{keyName}:</span>}
        <span className="text-muted-foreground text-[10px]">
          {isArray ? `Array(${entries.length})` : `{${entries.length}}`}
        </span>
      </button>
      {expanded && (
        <div>
          {entries.map(([k, v]) => (
            <JsonNode key={k} keyName={k} value={v} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export function StateInspector({ events, className }: StateInspectorProps) {
  const [collapsed, setCollapsed] = useState(false);

  // Build state from events — accumulate data from node_end events
  const state = useMemo(() => {
    const result: Record<string, unknown> = {};
    for (const event of events) {
      if (event.type === "node_end" && event.data) {
        const nodeId = event.nodeId ?? "unknown";
        result[nodeId] = {
          ...(result[nodeId] as Record<string, unknown> ?? {}),
          ...event.data,
        };
      }
      if (event.type === "stream" && event.data?.content) {
        const nodeId = event.nodeId ?? "unknown";
        const prev = (result[nodeId] as Record<string, unknown> ?? {});
        const prevContent = (prev._streamContent as string) ?? "";
        result[nodeId] = {
          ...prev,
          _streamContent: prevContent + String(event.data.content),
        };
      }
    }
    return result;
  }, [events]);

  if (Object.keys(state).length === 0) return null;

  return (
    <div className={cn("border-t border-border/50", className)}>
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
      >
        <Eye className="h-3 w-3" />
        State Inspector
        {collapsed ? <ChevronRight className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
      </button>
      {!collapsed && (
        <div className="px-2 pb-2 text-[11px] font-mono max-h-60 overflow-y-auto scrollbar-auto-hide">
          <JsonNode keyName={null} value={state} depth={0} />
        </div>
      )}
    </div>
  );
}
